import { AnswerModel } from '@/components/decision-tree/hooks';
import { format, isAfter, isValid, parse } from 'date-fns';
import { ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';
import { DateAnswer, DateValidation, isoDateFormat, uiDateFormat } from './types';

type ErrorType = { value: number; message: string };

type DateControllerProps = {
  validationValues?: DateValidation;
  saveAnswer: (payload: AnswerModel) => void;
  defaultValue?: DateAnswer;
};

export default function useDateController({ validationValues, saveAnswer, defaultValue }: DateControllerProps) {
  const defaultDate = (defaultValue as DateAnswer)?.value
    ? format((defaultValue as DateAnswer).value, uiDateFormat)
    : '';

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { date: defaultDate },
  });

  const handleChange = form.handleSubmit(({ date }) => {
    if (!date) return;

    const parsedDate = parse(date, uiDateFormat, new Date());
    if (!isValid(parsedDate)) return;

    const formattedDate: string = format(parsedDate, isoDateFormat);
    saveAnswer({ type: 'date', value: formattedDate });
  });

  const validationOptions: {
    validate?: (date: string) => boolean | string;
    max?: ErrorType;
  } = {};
  if (validationValues?.min || validationValues?.max)
    validationOptions.validate = (date: string) => {
      if (!date) return true;

      const today = new Date();
      const parsedDate = parse(date, uiDateFormat, today);
      if (!isValid(parsedDate)) return 'Invalid date';
      if (validationValues.min) {
        const parsedMin = parse(validationValues.min, 'yyyy-MM-dd', today);
        if (isValid(parsedMin) && isAfter(parsedMin, parsedDate)) return 'The value you entered seems unusually low';
      }
      if (validationValues.max) {
        const parsedMax = parse(validationValues.max, 'yyyy-MM-dd', today);
        if (isValid(parsedMax) && isAfter(parsedDate, parsedMax)) return 'The value you entered seems unusually high';
      }
      return true;
    };

  const onInputFn = async ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
    form.setValue('date', currentTarget.value, { shouldValidate: true });
    await form.trigger('date');
    if (!form.formState.errors.date) handleChange();
  };

  const onBlurFn = async ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
    form.setValue('date', currentTarget.value, { shouldValidate: true });
    await form.trigger('date');
    if (!form.formState.errors.date) handleChange();
  };

  return { onInputFn, form, validationOptions, defaultDate, onBlurFn };
}
